// src/pages/ProductivityChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Paper,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Stack,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SpinningModal from '../../components/ai-chat/SpinningModal';
import TaskButton from '../../components/TaskButtons/TaskButton';
import { styled } from '@mui/system';
import usePersistentState from '../../hooks/usePersistentState';
import { useAuth } from '../../context/AuthContext';

import userLogo from '../../assets/fig_CROP.png';
import agentLogo from '../../assets/fig_agent.png';

/* -------------------------- Config -------------------------- */
const API_BASE = 'https://gpt5api.swancreate.com';
const MAILGUN_SEND = 'https://mailgun.swancreate.com/send'; // JSON {to, subject, html, text}
const FIG_LOGO_URL = 'https://figk-12.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png';
const DI_ACCEPT = '.pdf,.docx,.rtf,.txt,.md,.csv,.xls,.xlsx,.xlsm';

/* --- Force DARK text regardless of TaskButton internals --- */
const DARK_TEXT_ALWAYS_SX = {
    '& .MuiButton-root, & .MuiButton-root *': { color: '#0f172a !important' },
};

/* --- Chat shell --- */
const ChatWindow = styled(Paper)(() => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    backgroundColor: '#f7faf9',
    color: '#0f172a',
}));
const ChatHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    borderBottom: '1px solid #e5e7eb',
}));
const ChatContent = styled(Box)(() => ({
    flex: 1,
    padding: 8,
    overflowY: 'auto',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    marginBottom: 8,
}));
const ChatInput = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f7faf9',
}));

/* -------------------------- Email helpers -------------------------- */
const EMAIL_TRIGGER = /\b(email|e-mail)\b|\bsend\b.*\b(email|mail)\b/i;
const CHART_TRIGGER = /\b(chart|graph)\b/i;

function getLoggedInEmail(user) {
    return user?.email || user?.Email || null;
}

// Strip "I can't email" boilerplate AND ugly ASCII bar blocks the model might emit.
function sanitizeForEmail(text) {
    let s = String(text || '');
    // remove “I can’t email” lines
    s = s.replace(/i do not have direct email capability.*$/gim, '')
        .replace(/i (?:can(?:not)?|can't) send emails?[^.]*\./gim, '');
    // remove common ASCII bar visual lines / headings
    s = s.replace(/^\s*(simple )?bar visualization.*$/gim, '');
    s = s.replace(/^[\s▁▂▃▄▅▆▇█━—\-–=•·]+$/gim, '');
    s = s.replace(/^\s*… \(see table for exact numbers\)\s*$/gim, '');
    // remove any line containing a long streak of block characters
    s = s.replace(/^.*[▁▂▃▄▅▆▇█]{5,}.*$/gim, '');
    return s.trim();
}

// Minimal Markdown -> HTML (tables, paragraphs, allow simple HTML blocks)
function mdToHtml(body) {
    const lines = String(body || '').split(/\r?\n/);
    const blocks = [];
    const bold = s => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    let i = 0;
    while (i < lines.length) {
        // pass-through of simple safe HTML blocks
        if (/^\s*<(table|div|ul|ol|img|figure)\b/i.test(lines[i])) {
            const buf = [];
            while (i < lines.length && !/^\s*$/.test(lines[i])) { buf.push(lines[i]); i++; }
            blocks.push(buf.join('\n'));
            while (i < lines.length && /^\s*$/.test(lines[i])) i++;
            continue;
        }
        // GitHub-style table
        if (lines[i].trim().startsWith('|')) {
            const start = i;
            if (i + 1 < lines.length && /\|?\s*:?-{2,}.*\|/.test(lines[i + 1])) {
                i += 2;
                const table = [lines[start], lines[start + 1]];
                while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
                    table.push(lines[i]); i++;
                }
                blocks.push(renderTable(table));
                while (i < lines.length && lines[i].trim() === '') i++;
                continue;
            }
        }
        // paragraph
        const buf = [];
        while (i < lines.length && lines[i].trim() !== '') { buf.push(lines[i]); i++; }
        if (buf.length) blocks.push(`<p style="margin:0 0 10px 0">${bold(buf.join('<br>'))}</p>`);
        while (i < lines.length && lines[i].trim() === '') i++;
    }
    return blocks.join('\n');

    function renderTable(tbl) {
        const headers = tbl[0].split('|').map(c => c.trim()).filter(Boolean);
        const rows = tbl.slice(2).map(r => r.split('|').map(c => c.trim()).filter(Boolean));
        const thead = `<tr>${headers.map(h =>
            `<th style="text-align:left;padding:10px 12px;border-bottom:1px solid #e6e9ef;background:#f8fafc;color:#0f172a;font-weight:600;">${h.replace(/\*\*(.+?)\*\*/g, '$1')}</th>`
        ).join('')}</tr>`;
        const tbody = rows.map((r, idx) =>
            `<tr style="background:${idx % 2 ? '#fcfcfd' : '#ffffff'}">
        ${r.map(c => `<td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;color:#0f172a;">${c.replace(/\*\*(.+?)\*\*/g, '$1')}</td>`).join('')}
       </tr>`
        ).join('');
        return `
      <div style="overflow:auto;margin:10px 0 12px 0">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0"
               style="width:100%;border:1px solid #e6e9ef;border-radius:10px;border-collapse:separate;">
          <thead>${thead}</thead>
          <tbody>${tbody}</tbody>
        </table>
      </div>`;
    }
}

/* ---- Series extraction (from table rows or “Name $123,456” lines) ---- */
function parseCurrencyToNumber(s) {
    const m = String(s).replace(/[^\d.-]/g, '');
    const n = parseFloat(m);
    return isFinite(n) ? n : NaN;
}
function extractSeriesFromText(text) {
    const series = [];
    const lines = String(text || '').split(/\r?\n/);

    // Table rows: | Name | $123 |
    for (const line of lines) {
        if (line.includes('|') && !/---|\bSchool Name\b/i.test(line)) {
            const raw = line.split('|').map(v => v.trim()).filter(Boolean);
            if (raw.length >= 2) {
                const label = raw[0];
                const valStr = raw[raw.length - 1];
                const val = parseCurrencyToNumber(valStr);
                if (label && isFinite(val)) series.push({ label, value: val });
            }
        }
    }

    // “Name $123,456” lines
    for (const line of lines) {
        if (/^\s*(Total|Source|User request)/i.test(line)) continue;
        const m = line.match(/^\s*([A-Za-z0-9().'&\-\/ ]+?)\s+\$([\d,]+(?:\.\d{1,2})?)/);
        if (m) {
            const label = m[1].trim();
            const val = parseCurrencyToNumber(m[2]);
            if (label && isFinite(val)) series.push({ label, value: val });
        }
    }

    // dedupe by label
    const byLabel = {};
    for (const { label, value } of series) {
        if (!byLabel[label] || value > byLabel[label]) byLabel[label] = value;
    }
    return Object.entries(byLabel)
        .map(([label, value]) => ({ label, value }))
        .filter(s => !/^Total\b/i.test(s.label))
        .sort((a, b) => b.value - a.value)
        .slice(0, 25);
}

function formatCurrencyUSD(n) {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
    } catch {
        return `$${Math.round(n).toLocaleString()}`;
    }
}

/* ---- Clean, email-safe bar chart (table with zebra rows & bars) ---- */
function buildInlineBarChartHTML(series, title = 'Visual: Forecasted Sales') {
    if (!series || !series.length) return '';
    const max = Math.max(...series.map(s => s.value)) || 1;

    const head = `
    <tr>
      <th style="text-align:left;padding:10px 12px;border-bottom:1px solid #e6e9ef;background:#f8fafc;color:#0f172a;font-weight:600;">School</th>
      <th style="text-align:left;padding:10px 12px;border-bottom:1px solid #e6e9ef;background:#f8fafc;color:#0f172a;font-weight:600;">Bar</th>
      <th style="text-align:right;padding:10px 12px;border-bottom:1px solid #e6e9ef;background:#f8fafc;color:#0f172a;font-weight:600;white-space:nowrap;">Amount</th>
    </tr>`;

    const rows = series.map((s, idx) => {
        const pct = Math.max(2, Math.round((s.value / max) * 100));
        return `
      <tr style="background:${idx % 2 ? '#fcfcfd' : '#ffffff'}">
        <td style="padding:10px 12px;color:#0f172a;">${escapeHtml(s.label)}</td>
        <td style="padding:10px 12px 10px 12px;">
          <div style="background:#e5e7eb;height:14px;border-radius:7px;overflow:hidden;">
            <div style="background:#84BD00;height:14px;width:${pct}%;border-radius:7px;"></div>
          </div>
        </td>
        <td style="padding:10px 12px;color:#0f172a;text-align:right;white-space:nowrap;">${formatCurrencyUSD(s.value)}</td>
      </tr>`;
    }).join('');

    return `
    <div style="margin:18px 0 10px 0;font-size:14px;color:#0f172a;font-weight:600;">${escapeHtml(title)}</div>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"
           style="border:1px solid #e6e9ef;border-radius:10px;border-collapse:separate;background:#ffffff;">
      <thead>${head}</thead>
      <tbody>${rows}</tbody>
    </table>`;
}

/* ---- FIG Email wrapper ---- */
function buildEmailHTML({ title, bodyHtml, ctaHref, userInstruction, chartHtml }) {
    const instructionBlock = userInstruction
        ? `<div style="margin-top:18px;padding:12px;border:1px dashed #e2e8f0;border-radius:8px;background:#f8fafc;color:#334155;font-size:12px;line-height:18px;">
         <strong style="color:#0f172a;">User request:</strong>
         <div>${escapeHtml(userInstruction)}</div>
       </div>`
        : '';
    const chartBlock = chartHtml ? `<div style="margin-top:14px">${chartHtml}</div>` : '';

    return `
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f5f7fa;padding:24px 0;">
    <tr><td align="center">
      <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="max-width:600px;width:100%;background:#ffffff;border:1px solid #e6e9ef;border-radius:16px;overflow:hidden;">
        <tr>
          <td align="center" style="padding:24px 24px 8px 24px">
            <!-- 3 x 3 inch logo container -->
            <table role="presentation" width="288" height="288" cellspacing="0" cellpadding="0" border="0"
                   style="width:3in;height:3in;max-width:288px;max-height:288px;border:1px solid #e6e9ef;border-radius:12px;background:#fff;overflow:hidden;">
              <tr><td align="center" valign="middle" style="padding:12px">
                <img src="${FIG_LOGO_URL}" width="230" height="230" alt="FIG K12"
                     style="display:block;border:0;outline:none;text-decoration:none;width:230px;height:230px;max-width:90%;max-height:90%;" />
              </td></tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:8px 24px 20px 24px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#0f172a;">
            <h1 style="margin:16px 0 8px 0;font-size:22px;line-height:28px;font-weight:700;color:#0f172a;">${escapeHtml(title)}</h1>
            ${bodyHtml}
            ${chartBlock}
            ${instructionBlock}
            ${ctaHref ? `
              <div style="text-align:center;margin:20px 0 0;">
                <a href="${ctaHref}" style="background:#84BD00;color:#000;text-decoration:none;display:inline-block;padding:10px 18px;border-radius:8px;font-weight:600;font-size:14px;">
                  Open in FIG
                </a>
              </div>` : ''}
            <hr style="border:none;border-top:1px solid #e6e9ef;margin:20px 0;">
            <p style="margin:0 0 4px 0;font-size:11px;line-height:16px;color:#94a3b8;">Sent by FIG K12 Productivity Chat</p>
            <p style="margin:0 0 16px 0;font-size:11px;line-height:16px;color:#94a3b8;">© ${new Date().getFullYear()} FIG K12</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>`;
}
function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/* ---- Subject helpers ---- */
function extractTitleFromAssistant(text) {
    const lines = String(text || '').split(/\r?\n/);
    for (const l of lines) {
        const m1 = l.match(/^\s*\*\*(.+?)\*\*/);     // **Title**
        const m2 = l.match(/^\s*#{1,3}\s+(.+)/);     // # Title
        if (m1) return m1[1];
        if (m2) return m2[1];
    }
    const first = lines.find(x => x.trim());
    return first ? first.replace(/\*\*/g, '').trim() : '';
}
function simplifySubject(title, userAsk) {
    const base = (title || '').trim() || (userAsk || '').trim();
    const cleaned = base.replace(/\s+/g, ' ').replace(/^[\W_]+|[\W_]+$/g, '').slice(0, 60);
    return cleaned || 'FIG Report';
}

async function sendEmail({ to, subject, html, text }) {
    await fetch(MAILGUN_SEND, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: '*/*' },
        body: JSON.stringify({ to, subject, html, text })
    });
}

/* -------------------------- Component -------------------------- */
export default function ProductivityChat({ onClose }) {
    const { user } = useAuth();
    const chatKey = `fig:chat:${user?.id || user?.UserId || 'anon'}`;

    // chat state
    const [conversation, setConversation, clearChat] = usePersistentState(chatKey, []);
    const [inputValue, setInputValue] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [displayedAssistant, setDisplayedAssistant] = useState('');
    const [lastAssistantText, setLastAssistantText] = useState('');
    const [lastUserText, setLastUserText] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);

    // contexts
    const [dataInput, setDataInput] = useState('');
    const [chatContext, setChatContext] = useState('');
    const [financeContext, setFinanceContext] = useState('');
    const [useFinance, setUseFinance] = useState(true);

    // agent thread
    const [threadId, setThreadId] = useState(null);

    // DI
    const [ingestedDocs, setIngestedDocs] = useState([]);
    const [docIds, setDocIds] = useState([]);
    const [docDigests, setDocDigests] = useState({});
    const [docPreviews, setDocPreviews] = useState({});

    // refs
    const chatContentRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    // email dialog state
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailTo, setEmailTo] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
    const [emailSending, setEmailSending] = useState(false);

    // toast
    const [toast, setToast] = useState({ open: false, type: 'success', msg: '' });

    /* -------------------------- scroll -------------------------- */
    useEffect(() => {
        chatContentRef.current?.scrollTo(0, chatContentRef.current.scrollHeight);
    }, [conversation, displayedAssistant, isThinking]);

    /* --------------- preload finance context --------------- */
    useEffect(() => {
        async function fetchContext() {
            const base = 'https://figazuresql.swancreate.com/api/swa/';
            const views = [
                'vw_aichat_ExpenseTotalsMonthly',
                'vw_aichat_IncomeTotalsMonthly',
                'vw_aichat_TopVendorsByDistrict',
                'vw_BudgetWorksheetTotals',
                'vw_KPI_MealsPerLaborHourBySiteMonth',
            ];
            try {
                const results = await Promise.all(views.map(v => axios.get(base + v)));
                let ctx = 'FINANCE DATA (use only when the question is about district financials/KPIs):\n\n';
                views.forEach((v, i) => { ctx += `[${v}]\n${JSON.stringify(results[i].data).slice(0, 3000)}\n\n`; });
                setFinanceContext(ctx);
            } catch {
                setFinanceContext('Note: Could not load one or more finance views.');
            } finally {
                setIsLoaded(true);
            }
        }
        fetchContext();
    }, []);

    /* ---------------------- typewriter effect ---------------------- */
    const animateTyping = (fullText) => {
        const words = String(fullText || '').split(' ');
        let idx = 0;
        setDisplayedAssistant('');
        const timer = setInterval(() => {
            setDisplayedAssistant(prev => (prev ? prev + ' ' : '') + words[idx]);
            idx++;
            if (idx >= words.length) {
                clearInterval(timer);
                setConversation(prev => [...prev, { role: 'assistant', content: fullText }]);
                setDisplayedAssistant('');
                setLastAssistantText(fullText);
            }
        }, 20);
    };

    /* ---------------------- DI: Upload & Ingest ---------------------- */
    function ensureTypedFile(file) {
        const guess = file.type && file.type !== '' ? file.type : 'application/octet-stream';
        return new File([file], file.name, { type: guess });
    }
    const onPickFiles = () => fileInputRef.current?.click();

    async function createDocDigest(docId, docName) {
        try {
            const { data } = await axios.post(`${API_BASE}/v1/chat`, {
                messages: [
                    { role: 'system', content: 'You compress documents into compact, factual digests with headings and bullet points.' },
                    { role: 'user', content: `Create a short digest of "${docName}". Include key entities, dates, totals, vendors/customers, and line-item highlights if present.` }
                ],
                docIds: [docId],
                topK: 12,
                temperature: 0.1,
                max_tokens: 700
            });
            const digest = data?.message?.content || '';
            setDocDigests(prev => ({ ...prev, [docId]: digest }));
        } catch (e) {
            console.warn('Doc digest failed:', e?.response?.data || e);
        }
    }

    const onFilesChosen = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setIsThinking(true);
        try {
            const uploads = [];
            for (const raw of files) {
                const f = ensureTypedFile(raw);
                const fd = new FormData();
                fd.append('file', f, f.name);
                const { data } = await axios.post(`${API_BASE}/v1/di/ingest`, fd, { headers: { Accept: 'application/json' } });
                uploads.push(data);
                const id = data?.doc?.id;
                if (id) {
                    const preview = data?.extract?.preview || '';
                    setDocPreviews(prev => ({ ...prev, [id]: preview }));
                    await createDocDigest(id, data.doc.name || f.name);
                }
            }
            setIngestedDocs(prev => [...prev, ...uploads.map(u => ({ doc: u.doc, extract: u.extract }))]);
            setDocIds(prev => [...prev, ...uploads.map(u => u?.doc?.id).filter(Boolean)]);
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message;
            setConversation(prev => [...prev, { role: 'assistant', content: `Upload failed: ${detail}` }]);
        } finally {
            setIsThinking(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const clearDocs = () => {
        setIngestedDocs([]); setDocIds([]); setDocDigests({}); setDocPreviews({});
    };

    /* ----------- targeted per-question extract (RAG) ----------- */
    async function targetedExtract(question) {
        if (!docIds.length) return '';
        try {
            const { data } = await axios.post(`${API_BASE}/v1/chat`, {
                messages: [
                    { role: 'system', content: 'Extract only the minimal facts relevant to the user query from the documents.' },
                    { role: 'user', content: `Question: ${question}\nReturn bullet points with page or table hints if available.` }
                ],
                docIds, topK: 8, temperature: 0.2, max_tokens: 500
            });
            return data?.message?.content || '';
        } catch (e) {
            console.warn('RAG extract failed:', e?.response?.data || e);
            return '';
        }
    }

    /* --------------------- Send → Agent & (optionally) Email --------------------- */
    const handleSend = async () => {
        if (isThinking || !isLoaded) return;
        const text = inputValue.trim();
        if (!text) return;

        const loggedInEmail = getLoggedInEmail(user);
        const wantsEmail = EMAIL_TRIGGER.test(text);
        const chartRequested = CHART_TRIGGER.test(text);
        setLastUserText(text);

        setInputValue('');
        setConversation(prev => [...prev, { role: 'user', content: text }]);
        setIsThinking(true);

        try {
            // Assemble context blocks
            const digests = docIds.map(id => docDigests[id]).filter(Boolean).join('\n\n');
            const targeted = await targetedExtract(text);
            const previews = (!digests && !targeted)
                ? docIds.map(id => {
                    const name = ingestedDocs.find(d => d.doc.id === id)?.doc?.name || id;
                    const prev = docPreviews[id];
                    return prev ? `[${name} — RAW PREVIEW]\n${prev}` : '';
                }).filter(Boolean).join('\n\n')
                : '';

            const parts = [];
            if (digests) parts.push(`DOC DIGESTS (use first):\n${digests}`);
            if (targeted) parts.push(`DOC RELEVANT EXTRACTS:\n${targeted}`);
            if (previews) parts.push(`DOC RAW PREVIEWS (fallback):\n${previews}`);
            if (useFinance && financeContext) parts.push(`FINANCE CONTEXT (only for finance/KPIs):\n${financeContext}`);
            if (chatContext) parts.push(`MANUAL NOTES:\n${chatContext}`);
            const contextBlock = parts.join('\n\n').trim();

            const agentPrompt = (contextBlock ? `
PRIORITY:
1) Answer from DOC DIGESTS and DOC RELEVANT EXTRACTS.
2) If those are empty, use DOC RAW PREVIEWS.
3) Only if still insufficient, consult FINANCE CONTEXT.
4) If still unknown, say "I don't know".

IMPORTANT EMAIL/CHART RULES:
- Provide a concise one-line title (≤ 60 chars) at the top of your answer that summarizes the purpose (used for email subject).
- If the user asks for a chart/graph, render the data as a Markdown table ONLY (no ASCII bar charts, no code-fence visuals). The platform will attach a polished HTML bar chart.
- Never include ASCII bars (e.g., ███).

CONTEXT BEGIN
${contextBlock}
CONTEXT END

USER QUESTION:
${text}
` : `
IMPORTANT EMAIL/CHART RULES:
- Provide a concise one-line title (≤ 60 chars) at the top of your answer that summarizes the purpose (used for email subject).
- If the user asks for a chart/graph, render the data as a Markdown table ONLY (no ASCII bar charts, no code-fence visuals). The platform will attach a polished HTML bar chart.
- Never include ASCII bars (e.g., ███).

${text}`);

            const { data } = await axios.post(`${API_BASE}/v1/research/chat`, {
                text: agentPrompt,
                threadId: threadId || undefined,
            });

            if (!threadId && data?.threadId) setThreadId(data.threadId);

            const msgs = Array.isArray(data?.messages) ? data.messages : [];
            let lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant')?.text || '[No response]';

            lastAssistant = sanitizeForEmail(lastAssistant);
            animateTyping(lastAssistant); // also sets lastAssistantText

            // Auto-email if the user asked
            if (wantsEmail && loggedInEmail) {
                const extracted = extractTitleFromAssistant(lastAssistant);
                const subject = simplifySubject(extracted, text);
                const cleanBodySource = sanitizeForEmail(lastAssistant);
                const bodyHtml = mdToHtml(cleanBodySource);

                // Build our polished HTML chart if user requested a chart/graph
                let chartHtml = '';
                if (chartRequested) {
                    const series = extractSeriesFromText(lastAssistant);
                    if (series.length) chartHtml = buildInlineBarChartHTML(series, 'Visual: Forecasted Sales');
                }

                const html = buildEmailHTML({
                    title: subject,
                    bodyHtml,
                    ctaHref: null,
                    userInstruction: text,
                    chartHtml,
                });
                const plain = `${subject}\n\n${lastAssistant}\n\nUser request: ${text}`;
                try {
                    await sendEmail({ to: loggedInEmail, subject, html, text: plain });
                    setConversation(prev => [...prev, { role: 'assistant', content: `✅ Emailed to **${loggedInEmail}** with subject “${subject}”.` }]);
                    setToast({ open: true, type: 'success', msg: `Email sent to ${loggedInEmail}` });
                } catch (e) {
                    const msg = e?.response?.data?.error || e?.message || 'unknown error';
                    setConversation(prev => [...prev, { role: 'assistant', content: `❌ I couldn’t send the email (${msg}).` }]);
                    setToast({ open: true, type: 'error', msg: `Email failed: ${msg}` });
                }
            } else if (wantsEmail && !loggedInEmail) {
                setConversation(prev => [...prev, { role: 'assistant', content: '📧 I’m ready to email this, but I can’t find your account email. Please add one to your profile.' }]);
            }
        } catch (err) {
            const detail = err?.response?.data?.error || err?.response?.data?.detail || err.message;
            setConversation(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
            setToast({ open: true, type: 'error', msg: detail });
        } finally {
            setIsThinking(false);
        }
    };

    /* ---------------------- Email dialog (manual button) ---------------------- */
    const openEmailDialog = () => {
        const to = getLoggedInEmail(user) || '';
        const extracted = extractTitleFromAssistant(lastAssistantText);
        const subject = simplifySubject(extracted, lastUserText);
        const cleanBodySource = sanitizeForEmail(lastAssistantText || '[No content]');
        const bodyHtml = mdToHtml(cleanBodySource);

        let chartHtml = '';
        if (CHART_TRIGGER.test(lastUserText)) {
            const series = extractSeriesFromText(lastAssistantText);
            if (series.length) chartHtml = buildInlineBarChartHTML(series, 'Visual: Forecasted Sales');
        }

        setEmailTo(to);
        setEmailSubject(subject);
        setEmailPreviewHtml(buildEmailHTML({
            title: subject,
            bodyHtml,
            ctaHref: null,
            userInstruction: lastUserText,
            chartHtml,
        }));
        setEmailOpen(true);
    };

    const sendEmailNow = async () => {
        setEmailSending(true);
        try {
            const plain = `${emailSubject}\n\n${lastAssistantText}\n\nUser request: ${lastUserText}`;
            await sendEmail({ to: emailTo, subject: emailSubject, html: emailPreviewHtml, text: plain });
            setEmailOpen(false);
            setToast({ open: true, type: 'success', msg: `Email sent to ${emailTo}` });
            setConversation(prev => [...prev, { role: 'assistant', content: `✅ Emailed to **${emailTo}** with subject “${emailSubject}”.` }]);
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || 'unknown error';
            setToast({ open: true, type: 'error', msg });
        } finally {
            setEmailSending(false);
        }
    };

    /* ---------------------- manual context dialog ---------------------- */
    const [isDataModalOpen, setIsDataModalOpen] = useState(false);
    const handleAddData = () => {
        if (!dataInput.trim()) return;
        setChatContext(dataInput.trim());
        setDataInput('');
        setIsDataModalOpen(false);
    };

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <ChatWindow elevation={5}>
                {isThinking && (
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                        <SpinningModal size={80} />
                    </Box>
                )}

                {/* header */}
                <ChatHeader>
                    <Box>
                        <Typography variant="h6" sx={{ fontSize: '22pt', mb: 0, color: '#0f172a' }}>
                            Productivity Chat
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#334155' }}>
                            USDA & District Finance Assistant (Agent + DI Context)
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <Button variant="contained" color="primary" onClick={() => setIsDataModalOpen(true)} sx={{ textTransform: 'none' }}>
                                Add Chat Context Data
                            </Button>
                            <Button variant="contained" color="secondary" onClick={clearChat} sx={{ textTransform: 'none' }}>
                                Clear Chat
                            </Button>
                            <Button variant="contained" onClick={onPickFiles} sx={{ textTransform: 'none' }}>
                                Upload Docs (DI)
                            </Button>
                            <Button variant="outlined" onClick={clearDocs} sx={{ textTransform: 'none' }}>
                                Clear Docs
                            </Button>

                            {/* manual email button */}
                            <Button
                                variant="contained"
                                color="success"
                                onClick={openEmailDialog}
                                disabled={!lastAssistantText}
                                sx={{ textTransform: 'none' }}
                            >
                                Email latest result
                            </Button>

                            {threadId && <Chip label={`Thread: ${threadId}`} size="small" sx={{ ml: 1 }} />}
                            <FormControlLabel
                                control={<Switch checked={useFinance} onChange={(e) => setUseFinance(e.target.checked)} color="primary" />}
                                label="Use Finance Context"
                            />
                        </Box>

                        {/* Ingested docs list */}
                        {!!ingestedDocs.length && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                                {ingestedDocs.map(({ doc, extract }) => {
                                    const hasCtx = !!(docDigests[doc.id] || docPreviews[doc.id]);
                                    return (
                                        <Chip
                                            key={doc.id}
                                            label={`${doc.name || doc.id}${extract?.page_count ? ` • ${extract.page_count}p` : ''}${hasCtx ? ' • ✅' : ' • ⏳'}`}
                                            variant="outlined"
                                        />
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    {onClose && (
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </ChatHeader>

                {/* conversation */}
                <ChatContent ref={chatContentRef}>
                    {conversation.map((msg, i) => (
                        <Box key={i} display="flex" alignItems="flex-start" mb={1}>
                            <Avatar src={msg.role === 'assistant' ? agentLogo : userLogo} sx={{ mr: 1, width: 40, height: 40 }} />
                            <Box sx={DARK_TEXT_ALWAYS_SX}>
                                <TaskButton content={msg.content} initialStatus="Initial" variant="figIce" />
                            </Box>
                        </Box>
                    ))}

                    {displayedAssistant && (
                        <Box display="flex" alignItems="flex-start" mb={1}>
                            <Avatar src={agentLogo} sx={{ mr: 1, width: 40, height: 40 }} />
                            <Box sx={DARK_TEXT_ALWAYS_SX}>
                                <TaskButton content={displayedAssistant} initialStatus="Initial" variant="figIce" />
                            </Box>
                        </Box>
                    )}
                </ChatContent>

                {/* input */}
                <ChatInput>
                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        placeholder={isLoaded ? 'Type your message… (e.g., “email me a chart of NSLP by site”)' : 'Loading data…'}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        disabled={!isLoaded}
                        sx={{
                            '& .MuiInputBase-input': { color: '#0f172a' },
                            '& .MuiInputLabel-root': { color: '#64748b' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' }
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />
                    <Button variant="contained" color="primary" sx={{ mt: 1, alignSelf: 'flex-end' }} onClick={handleSend} disabled={!isLoaded}>
                        Send
                    </Button>
                </ChatInput>
            </ChatWindow>

            {/* Add-context dialog */}
            <Dialog open={isDataModalOpen} onClose={() => setIsDataModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add Chat Context Data</DialogTitle>
                <DialogContent>
                    <TextField
                        value={dataInput}
                        onChange={e => setDataInput(e.target.value)}
                        placeholder="Enter chat context data…"
                        multiline
                        minRows={4}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDataModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddData} variant="contained" color="primary">Add Data</Button>
                </DialogActions>
            </Dialog>

            {/* Email dialog */}
            <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Email latest result</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                        <TextField label="To" value={emailTo} onChange={e => setEmailTo(e.target.value)} />
                        <TextField
                            label="Subject"
                            value={emailSubject}
                            onChange={e => {
                                const subj = e.target.value;
                                setEmailSubject(subj);
                                const bodyHtml = mdToHtml(sanitizeForEmail(lastAssistantText || '[No content]'));
                                let chartHtml = '';
                                if (CHART_TRIGGER.test(lastUserText)) {
                                    const series = extractSeriesFromText(lastAssistantText);
                                    if (series.length) chartHtml = buildInlineBarChartHTML(series, 'Visual: Forecasted Sales');
                                }
                                setEmailPreviewHtml(buildEmailHTML({ title: subj, bodyHtml, ctaHref: null, userInstruction: lastUserText, chartHtml }));
                            }}
                        />
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', background: '#f8fafc' }}>
                        <div dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailOpen(false)}>Cancel</Button>
                    <Button onClick={sendEmailNow} variant="contained" disabled={emailSending || !emailTo}>
                        {emailSending ? 'Sending…' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* hidden file input for DI uploads — matches curl (field name "file") */}
            <input
                ref={fileInputRef}
                type="file"
                accept={DI_ACCEPT}
                multiple
                style={{ display: 'none' }}
                onChange={onFilesChosen}
            />

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast(t => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setToast(t => ({ ...t, open: false }))} severity={toast.type} sx={{ width: '100%' }}>
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
